import React, { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, ChevronLeft, ChevronRight, FileSpreadsheet, Users, CheckCircle2, Clock, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicSnapshot() {
  const { token } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [project, setProject] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 50 };
      if (search) params.search = search;
      if (project) params.project = project;
      if (status) params.status = status;
      const res = await axios.get(`${API}/tracker/public/${token}`, { params });
      setData(res.data);
      setError(null);
    } catch (err) {
      setError(err.response?.status === 404 ? "This link has expired or is invalid." : "Failed to load data.");
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [token, page, project, status]);
  useEffect(() => { const t = setTimeout(() => fetchData(), 300); return () => clearTimeout(t); }, [search]);

  if (error) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="text-center">
        <FileSpreadsheet className="w-12 h-12 text-slate-300 mx-auto mb-3" />
        <p className="text-lg font-semibold text-slate-700">{error}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="public-snapshot-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold text-slate-900">Graphikpro — Project Tracker</h1>
            <p className="text-xs text-slate-500">{data?.snapshot_name || "Loading..."} {data?.uploaded_at ? `· Updated ${new Date(data.uploaded_at).toLocaleDateString()}` : ""}</p>
          </div>
          {data?.stats && (
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1.5 text-sm"><FileSpreadsheet className="w-4 h-4 text-blue-500" /><span className="font-semibold text-slate-700">{data.stats.total}</span><span className="text-slate-400">tasks</span></div>
              <div className="flex items-center gap-1.5 text-sm"><CheckCircle2 className="w-4 h-4 text-emerald-500" /><span className="font-semibold text-emerald-700">{data.stats.completed}</span><span className="text-slate-400">done</span></div>
              <div className="flex items-center gap-1.5 text-sm"><Clock className="w-4 h-4 text-amber-500" /><span className="font-semibold text-amber-700">{data.stats.in_progress}</span><span className="text-slate-400">wip</span></div>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-4">
        {/* Filters */}
        <div className="bg-white rounded-xl border p-3 mb-4 flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <Input className="h-8 pl-8 text-[12px]" placeholder="Search project, episode, resource..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          {data?.filters?.projects?.length > 0 && (
            <Select value={project || "all"} onValueChange={v => { setProject(v === "all" ? "" : v); setPage(1); }}>
              <SelectTrigger className="h-8 w-48 text-[12px]"><SelectValue placeholder="All Projects" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Projects</SelectItem>{data.filters.projects.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <Select value={status || "all"} onValueChange={v => { setStatus(v === "all" ? "" : v); setPage(1); }}>
            <SelectTrigger className="h-8 w-32 text-[12px]"><SelectValue placeholder="All Status" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Status</SelectItem><SelectItem value="Completed">Completed</SelectItem><SelectItem value="In Progress">In Progress</SelectItem></SelectContent>
          </Select>
          <span className="text-[11px] text-slate-400 ml-auto">{data?.total || 0} results</span>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border overflow-hidden">
          {loading && !data ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-blue-400" /></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-400">#</th>
                    <th className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-400">Project</th>
                    <th className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-400">Episode</th>
                    <th className="px-3 py-2.5 text-center text-[10px] font-semibold uppercase tracking-wider text-slate-400">Task</th>
                    <th className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-400">Resource</th>
                    <th className="px-3 py-2.5 text-center text-[10px] font-semibold uppercase tracking-wider text-slate-400">Language</th>
                    <th className="px-3 py-2.5 text-center text-[10px] font-semibold uppercase tracking-wider text-slate-400">Due Date</th>
                    <th className="px-3 py-2.5 text-center text-[10px] font-semibold uppercase tracking-wider text-slate-400">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.tasks || []).map((t, i) => (
                    <tr key={t.id || i} className="border-b hover:bg-slate-50">
                      <td className="px-3 py-2 text-[11px] text-slate-400">{(page - 1) * 50 + i + 1}</td>
                      <td className="px-3 py-2">
                        <p className="text-[12px] font-medium text-slate-700">{t.main_title || "-"}</p>
                        {t.season_number && <span className="text-[9px] text-slate-400">S{t.season_number}</span>}
                      </td>
                      <td className="px-3 py-2 text-[12px] text-slate-500 truncate max-w-[140px]">{t.episode_title || "-"}</td>
                      <td className="px-3 py-2 text-center"><Badge variant="outline" className="text-[10px]">{t.task || "-"}</Badge></td>
                      <td className="px-3 py-2 text-[12px] text-slate-600">{t.resource_name || t.resource || "-"}</td>
                      <td className="px-3 py-2 text-center text-[12px] text-slate-500">{t.language || "-"}</td>
                      <td className="px-3 py-2 text-center text-[11px] text-slate-500">{t.due_date || "-"}</td>
                      <td className="px-3 py-2 text-center">
                        <Badge className={`border-0 text-[10px] ${t.task_status === "Completed" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{t.task_status || "In Progress"}</Badge>
                      </td>
                    </tr>
                  ))}
                  {(data?.tasks || []).length === 0 && (
                    <tr><td colSpan={8} className="text-center py-10 text-sm text-slate-400">No tasks found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {data && data.total_pages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-4">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
            <span className="text-xs text-slate-500">Page {page} of {data.total_pages}</span>
            <Button variant="outline" size="sm" disabled={page >= data.total_pages} onClick={() => setPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
          </div>
        )}

        <p className="text-center text-[10px] text-slate-400 mt-8">Powered by Graphikpro Pvt Ltd.</p>
      </div>
    </div>
  );
}
